import React, { useState, useEffect, useCallback } from 'react';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Alert,
  Snackbar,
  Grid,
  IconButton,
  Chip,
  Tooltip,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
  ExpandMore as ExpandMoreIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { policyService } from '../services/policyService';
import { Policy, CreatePolicyData } from '../services/policyService';

const Policies: React.FC = () => {
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState<Policy | null>(null);
  const [selectedPolicyVersions, setSelectedPolicyVersions] = useState<Policy[]>([]);
  const [formData, setFormData] = useState<CreatePolicyData>({
    title: '',
    slug: '',
    content: '',
    description: '',
    isActive: true,
  });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Load policies
  const loadPolicies = useCallback(async () => {
    setLoading(true);
    try {
      const response = await policyService.getPolicies({
        page,
        limit: 10,
        search: searchTerm || undefined,
        isActive: statusFilter === 'active' ? true : statusFilter === 'inactive' ? false : undefined,
        isExpired: false, // Only show current versions
      });
      
      if (response.success) {
        setPolicies(response.data.policies);
        setTotalPages(response.data.pagination.totalPages);
      } else {
        setError(response.message || 'Failed to load policies');
      }
    } catch (err) {
      setError('Failed to load policies');
    } finally {
      setLoading(false);
    }
  }, [page, searchTerm, statusFilter]);

  useEffect(() => {
    loadPolicies();
  }, [loadPolicies]);

  // Handle dialog open
  const handleOpen = (policy?: Policy) => {
    if (policy) {
      setEditingPolicy(policy);
      setFormData({
        title: policy.title,
        slug: policy.slug,
        content: policy.content,
        description: policy.description || '',
        isActive: policy.isActive,
      });
    } else {
      setEditingPolicy(null);
      setFormData({
        title: '',
        slug: '',
        content: '',
        description: '',
        isActive: true,
      });
    }
    setOpen(true);
  };

  // Handle dialog close
  const handleClose = () => {
    setOpen(false);
    setEditingPolicy(null);
    setFormData({
      title: '',
      slug: '',
      content: '',
      description: '',
      isActive: true,
    });
  };

  // Handle version history dialog
  const handleOpenVersions = async (policy: Policy) => {
    try {
      setLoading(true);
      const response = await policyService.getPolicyVersions(policy.slug);
      if (response.success && response.data?.versions) {
        setSelectedPolicyVersions(response.data.versions);
        setVersionsOpen(true);
      } else {
        setError('Failed to load version history');
      }
    } catch (err) {
      setError('Failed to load version history');
    } finally {
      setLoading(false);
    }
  };

  // Handle form input changes
  const handleInputChange = (field: keyof CreatePolicyData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Auto-generate slug from title
    if (field === 'title' && !editingPolicy) {
      const slug = value.toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .trim();
      setFormData(prev => ({ ...prev, slug }));
    }
  };

  // Handle save
  const handleSave = async () => {
    if (!formData.title || !formData.content) {
      setError('Title and content are required');
      return;
    }

    setLoading(true);
    try {
      let response;
      if (editingPolicy) {
        // Update creates a new version
        response = await policyService.updatePolicy(editingPolicy.id, formData);
      } else {
        response = await policyService.createPolicy(formData);
      }

      if (response.success) {
        setSuccess(editingPolicy ? `Policy updated successfully. ${response.message}` : 'Policy created successfully');
        handleClose();
        loadPolicies();
      } else {
        setError(response.message || 'Failed to save policy');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save policy');
    } finally {
      setLoading(false);
    }
  };

  // Handle delete
  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this policy? This will expire all versions.')) return;

    setLoading(true);
    try {
      const response = await policyService.deletePolicy(id);
      if (response.success) {
        setSuccess('Policy deleted successfully');
        loadPolicies();
      } else {
        setError(response.message || 'Failed to delete policy');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete policy');
    } finally {
      setLoading(false);
    }
  };

  // Handle status toggle
  const handleToggleStatus = async (id: number, isActive: boolean) => {
    setLoading(true);
    try {
      const response = await policyService.togglePolicyStatus(id, isActive);
      if (response.success) {
        setSuccess(`Policy ${isActive ? 'activated' : 'deactivated'} successfully`);
        loadPolicies();
      } else {
        setError(response.message || 'Failed to update policy status');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update policy status');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {/* Header */}
      {/* <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Policy Management</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpen()}
        >
          Add Policy
        </Button>
      </Box> */}

      {/* Filters */}
      {/* <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search policies..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="outlined"
                onClick={loadPolicies}
                fullWidth
              >
                Search
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card> */}

      {/* Policies Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                {/* <TableCell>Slug</TableCell> */}
                <TableCell>Version</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : policies.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No policies found
                  </TableCell>
                </TableRow>
              ) : (
                policies.map((policy) => (
                  <TableRow key={policy.id}>
                    <TableCell>
                      <Typography variant="subtitle2" noWrap>
                        {policy.title}
                      </Typography>
                    </TableCell>
                    {/* <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {policy.slug}
                      </Typography>
                    </TableCell> */}
                    <TableCell>
                      <Chip label={policy.versionId} size="small" color="primary" />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={policy.isActive ? 'Active' : 'Inactive'}
                        color={policy.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {new Date(policy.createdAt).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="View Version History">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenVersions(policy)}
                          >
                            <HistoryIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit (Create New Version)">
                          <IconButton
                            size="small"
                            onClick={() => handleOpen(policy)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        {/* <Tooltip title={policy.isActive ? 'Deactivate' : 'Activate'}> 
                          <IconButton
                            size="small"
                            onClick={() => handleToggleStatus(policy.id, !policy.isActive)}
                          >
                            {policy.isActive ? <ToggleOnIcon /> : <ToggleOffIcon />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(policy.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>*/}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Pagination */}
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, newPage) => setPage(newPage)}
              color="primary"
            />
          </Box>
        )}
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{editingPolicy ? `Edit Policy (Will create version ${editingPolicy.versionId})` : 'Add New Policy'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Title"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  required
                  disabled={!!editingPolicy}
                />
              </Grid>
              
              {/* <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Slug"
                  value={formData.slug}
                  onChange={(e) => handleInputChange('slug', e.target.value)}
                  required
                  disabled={!!editingPolicy}
                  helperText={editingPolicy ? 'Slug cannot be changed when updating' : 'Auto-generated from title'}
                />
              </Grid> */}

              {/* <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Description"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                />
              </Grid> */}

              {/* Content with CKEditor */}
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mb: 1 }}>Content</Typography>
                <Box sx={{ border: '1px solid #ccc', borderRadius: 1 }}>
                  <CKEditor
                    editor={ClassicEditor}
                    data={formData.content}
                    onChange={(event, editor) => {
                      const data = editor.getData();
                      handleInputChange('content', data);
                    }}
                    config={{
                      toolbar: [
                        'heading', '|',
                        'bold', 'italic', 'underline', '|',
                        'bulletedList', 'numberedList', '|',
                        'link', 'blockQuote', '|',
                        'undo', 'redo'
                      ],
                      placeholder: 'Write your policy content here...'
                    }}
                  />
                </Box>
              </Grid>

              {/* Settings */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={(e) => handleInputChange('isActive', e.target.checked)}
                    />
                  }
                  label="Active"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={loading}>
            {loading ? 'Saving...' : editingPolicy ? 'Update (New Version)' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Version History Dialog */}
      <Dialog open={versionsOpen} onClose={() => setVersionsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Version History</DialogTitle>
        <DialogContent>
          <List>
            {selectedPolicyVersions.map((version) => (
              <ListItem
                key={version.id}
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  mb: 1,
                  bgcolor: version.isExpired ? '#f5f5f5' : '#e8f5e9',
                }}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="subtitle1">
                        Version {version.versionId}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {version.isExpired && (
                          <Chip label="Expired" size="small" color="default" />
                        )}
                        {version.isActive && !version.isExpired && (
                          <Chip label="Current" size="small" color="success" />
                        )}
                      </Box>
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="caption" display="block">
                        Created: {new Date(version.createdAt).toLocaleString()}
                      </Typography>
                      {version.expiredAt && (
                        <Typography variant="caption" display="block" color="error">
                          Expired: {new Date(version.expiredAt).toLocaleString()}
                        </Typography>
                      )}
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVersionsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbars */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
      
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Policies;
